import {
  HTMLAttributes,
  KeyboardEventHandler,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useForwardedRef, useMeasure } from '@adsk/alloy-react-helpers';
import { RenderIconType } from '@adsk/alloy-react-icon';

import { LEFT_PADDING, RIGHT_PADDING } from '../consts';
import { firefoxNumberInputWorkaround, getIsFirefox } from '../helpers';
import { TextInputType } from '../types';

type UseTextInputProps = {
  type?: TextInputType;
  renderLeft?: RenderIconType;
  renderRight?: RenderIconType;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  ref?: React.ForwardedRef<HTMLInputElement>;
  showCount?: boolean;
  softLimit?: number;
  maxLength?: number;
  showCountThreshold?: number;
  value?: string;
  defaultValue?: string | number | readonly string[];
};

export default ({
  type,
  renderLeft,
  renderRight,
  onKeyDown,
  ref,
  showCount,
  softLimit,
  maxLength,
  showCountThreshold,
  onChange,
  value,
  defaultValue,
}: UseTextInputProps) => {
  const inputRef = useForwardedRef<HTMLInputElement | null>(ref || null);

  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const [charCount, setCharCount] = useState(0);
  const [paddingLeft, setPaddingLeft] = useState(LEFT_PADDING);
  const [paddingRight, setPaddingRight] = useState(RIGHT_PADDING);
  const divRef = useCallback((element: HTMLDivElement | null) => {
    if (!element) {
      return;
    }

    const divStyle = getComputedStyle(element);

    if (divStyle.paddingLeft) {
      setPaddingLeft(parseInt(divStyle.paddingLeft));
    }

    if (divStyle.paddingRight) {
      setPaddingRight(parseInt(divStyle.paddingRight));
    }
  }, []);

  const { width: leftWidth } = useMeasure(leftRef);
  const { width: rightWidth } = useMeasure(rightRef);

  useLayoutEffect(() => {
    if (renderLeft) {
      if (leftWidth && inputRef.current) {
        const newPadding = paddingLeft + leftWidth + 5;
        inputRef.current.style.paddingLeft = `${newPadding}px`;
      }
    }
  }, [inputRef, renderLeft, leftWidth, paddingLeft]);

  useLayoutEffect(() => {
    if (value !== undefined && value !== null) {
      setCharCount(value.length);
    }
  }, [value]);

  useLayoutEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null) {
      setCharCount(defaultValue.toString().length);
    }
    // we only want to do this on mount since it's the default value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (getIsFirefox() && type === 'number') {
        firefoxNumberInputWorkaround({ event, inputRef });
      }

      onKeyDown?.(event);
    },
    [inputRef, onKeyDown, type],
  );

  const shouldRenderCount = useMemo(() => {
    const limit = showCount !== false && (softLimit || maxLength);

    return (
      limit &&
      (!showCountThreshold ||
        (showCountThreshold && limit - charCount <= showCountThreshold))
    );
  }, [showCount, maxLength, softLimit, charCount, showCountThreshold]);

  useLayoutEffect(() => {
    if (renderRight || shouldRenderCount) {
      let width = rightWidth;

      // this is to handle cases where rightRef.current has mutated and
      // useMeasure does not know about it
      if (!width) {
        width = 0;

        if (rightRef?.current) {
          width = (rightRef.current as HTMLElement).getBoundingClientRect()
            ?.width;
        }
      }

      if (width && inputRef.current) {
        const newPadding = paddingRight + width + 8;
        inputRef.current.style.paddingRight = `${newPadding}px`;
      }
    } else if (inputRef.current) {
      inputRef.current.style.paddingRight = `${paddingRight + 8}px`;
    }
  }, [inputRef, renderRight, shouldRenderCount, rightWidth, paddingRight]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined || value === null) {
        if (event.target.value) {
          setCharCount(event.target.value.length);
        } else {
          setCharCount(0);
        }
      }

      onChange?.(event);
    },
    [onChange, value],
  );

  const getInputProps: () => HTMLAttributes<HTMLInputElement> =
    useCallback(() => {
      return {
        ref: inputRef,
        type,
        onKeyDown: handleKeyDown,
        onChange: handleChange,
        dir: 'auto',
        value,
        defaultValue,
        style: { paddingLeft, paddingRight },
      };
    }, [
      inputRef,
      type,
      handleKeyDown,
      handleChange,
      value,
      defaultValue,
      paddingLeft,
      paddingRight,
    ]);

  return {
    refs: {
      divRef,
      leftRef,
      rightRef,
    },
    propGetters: {
      getInputProps,
    },
    padding: { paddingLeft, paddingRight },
    charCount,
    shouldRenderCount,
  };
};
